import React, { useEffect, useState } from "react";
import { Download, RefreshCw, WifiOff, X } from "lucide-react";

// Registers the service worker, surfaces install & update prompts, and an offline indicator.
export default function PwaManager() {
  const [deferred, setDeferred] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [updateReady, setUpdateReady] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [offline, setOffline] = useState(!navigator.onLine);

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/service-worker.js").then((reg) => {
        if (reg.waiting) { setWaitingWorker(reg.waiting); setUpdateReady(true); }
        reg.addEventListener("updatefound", () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener("statechange", () => {
            if (nw.state === "installed" && navigator.serviceWorker.controller) { setWaitingWorker(nw); setUpdateReady(true); }
          });
        });
      }).catch(() => {});
      let refreshing = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => { if (!refreshing) { refreshing = true; window.location.reload(); } });
    }
    const onBip = (e) => { e.preventDefault(); setDeferred(e); setShowInstall(true); };
    window.addEventListener("beforeinstallprompt", onBip);
    const on = () => setOffline(false), off = () => setOffline(true);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("beforeinstallprompt", onBip); window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  const install = async () => { if (!deferred) return; deferred.prompt(); await deferred.userChoice; setDeferred(null); setShowInstall(false); };
  const applyUpdate = () => { if (waitingWorker) waitingWorker.postMessage({ type: "SKIP_WAITING" }); setUpdateReady(false); };

  return (
    <>
      {offline && (
        <div data-testid="offline-indicator" className="fixed top-0 inset-x-0 z-[70] bg-amber-500/90 text-black text-xs text-center py-1.5 flex items-center justify-center gap-2">
          <WifiOff size={13} /> You're offline — some features are unavailable. Cached pages still work.
        </div>
      )}
      {updateReady && (
        <div data-testid="update-banner" className="fixed bottom-5 left-5 z-[70] glass-dark rounded-xl px-4 py-3 flex items-center gap-3 max-w-xs" style={{ borderColor: "rgba(0,229,255,0.4)" }}>
          <RefreshCw size={16} className="text-pool" />
          <div className="flex-1"><p className="text-sm">A new version of The D is ready.</p></div>
          <button onClick={applyUpdate} className="btn-gold px-3 py-1.5 text-xs" data-testid="update-apply">Refresh</button>
        </div>
      )}
      {showInstall && (
        <div data-testid="install-banner" className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[70] glass-dark rounded-full pl-4 pr-2 py-2 flex items-center gap-3" style={{ borderColor: "rgba(212,175,55,0.4)" }}>
          <span className="text-xs text-gold tracking-label uppercase">Install The D</span>
          <button onClick={install} className="btn-gold px-4 py-1.5 text-xs flex items-center gap-1.5" data-testid="install-accept"><Download size={13} /> Install</button>
          <button onClick={() => setShowInstall(false)} aria-label="Dismiss" className="p-1.5 text-muted-foreground hover:text-foreground"><X size={14} /></button>
        </div>
      )}
    </>
  );
}
